import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { FormRadioGroup } from '@/components/forms/form-radio-group';
import { MultiSelectOption } from '@/components/ui/multi-select';
import {
  buildCoachSelectOptions,
  buildNameLikeSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
} from '@/components/ui/multi-select-utils';
import { useFeatureFlag } from '@/hooks/use-feature-flags';
import {
  coachesTypeaheadSearch,
  useAllLearningPaths,
  useAvailableCoachesSearch,
  useClassTimeOptions,
  userTypeaheadSearch,
} from '@/lib/api-call-hooks';
import { EmptyArray, getCoach, getCoaches } from '@/lib/api-calls';
import { AllSelectTeacherTypes, AllUpdateTeacherTypeValues, CoachesTypeaheadSearchOptions } from '@/lib/coach-utils';
import { formUpdateBulkActionsSchema } from '@/lib/student-group-utils';
import { distinct, formatPlainDate, isNotNullish } from '@airreading/common';
import {
  CoachStatus,
  EditStudentGroupBulkActions,
  GetCoachesModifier,
  ScheduleFromStudentGroupBulkActions,
  ScheduleFromStudentGroupBulkActionsLabelMap,
  StudentGroupWithScheduleAndLevel,
} from '@airreading/common/data';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

export interface BulkUpdateClassesFormProps {
  form: UseFormReturn<z.infer<typeof formUpdateBulkActionsSchema>>;
  bulkType: EditStudentGroupBulkActions;
  schoolId: string;
  selectableCoaches?: MultiSelectOption[];
  studentGroups: StudentGroupWithScheduleAndLevel[];
  startPlainDate: Temporal.PlainDate;
  endPlainDate: Temporal.PlainDate;
  isOneTimeClass?: boolean;
}

export const BulkUpdateClassesForm = ({
  form,
  bulkType,
  schoolId,
  selectableCoaches = undefined,
  studentGroups,
  startPlainDate,
  endPlainDate,
  isOneTimeClass = false,
}: BulkUpdateClassesFormProps) => {
  const queryClient = useQueryClient();
  const teacherAvailabilityV2Enabled = useFeatureFlag('teacher-availability-v2', false);

  const [selectedLearningPathId, setSelectedLearningPathId] = useState<string | undefined>(undefined);
  const [selectedUpdateType, setSelectedUpdateType] = useState<string | undefined>(
    ScheduleFromStudentGroupBulkActions.FromNow,
  );
  const [selectedTeacherType, setSelectedTeacherType] = useState<string | undefined>(
    AllUpdateTeacherTypeValues.Available,
  );

  const { learningPaths: availableLearningPaths, isPending: isLoadingLearningPaths } = useAllLearningPaths();
  const learningPathOptions: MultiSelectOption[] = availableLearningPaths.map(buildNameLikeSelectOptions);

  const allUpdateTypes = Object.values(ScheduleFromStudentGroupBulkActions).map((value) => ({
    label: ScheduleFromStudentGroupBulkActionsLabelMap[value],
    value,
  }));

  const { watch, setValue } = form;

  const updateType = watch('updateType');
  const overrideStartPlainDate = watch('startPlainDate');
  const overrideEndPlainDate = watch('endPlainDate');
  const selectedScheduleId = watch('schedule')?.value;
  const selectedClassTimeId = watch('classTime')?.value;

  const {
    data: schedules,
    isPending: isLoadingSchedules,
    classTimeOptions,
  } = useClassTimeOptions({
    schoolId,
  });
  const scheduleOptions: MultiSelectOption[] = useMemo(
    () => schedules?.map(buildNameLikeSelectOptions) || EmptyArray,
    [schedules],
  );
  const selectedSchedule = schedules?.find((s) => s.id === selectedScheduleId);

  const filteredClassTimeOptions = useMemo(() => {
    if (!selectedSchedule) return [];

    return classTimeOptions.filter((ct) =>
      selectedSchedule.classTimes.some(({ id: scheduleClassTimeId }) => scheduleClassTimeId === ct.value),
    );
  }, [classTimeOptions, selectedSchedule]);

  useEffect(() => {
    if (bulkType !== EditStudentGroupBulkActions.RecreateSessions) {
      return;
    }

    if (!selectedClassTimeId) {
      return;
    }

    const selectedClassTimeIsValid = filteredClassTimeOptions.some(
      (classTime) => classTime.value === selectedClassTimeId,
    );
    if (selectedClassTimeIsValid) {
      return;
    }

    // Schedule/class time changed; clear stale selections so available teachers re-evaluates from current form state.
    setValue('classTime', emptySelectOption, { shouldDirty: true, shouldValidate: true });
    setValue('coachAvailable', emptySelectOption, { shouldDirty: true, shouldValidate: true });
    setValue('coachAll', emptySelectOption, { shouldDirty: true, shouldValidate: true });
  }, [bulkType, filteredClassTimeOptions, selectedClassTimeId, setValue]);

  useEffect(() => {
    if (selectedUpdateType !== updateType) {
      setValue('startPlainDate', startPlainDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('endPlainDate', endPlainDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setSelectedUpdateType(updateType);
    }
  }, [updateType, setValue, selectedUpdateType, startPlainDate, endPlainDate]);

  useEffect(() => {
    // Reset coach dropdowns when teacher type changes
    const sub = watch((value, { name }) => {
      if (name === 'teacherType' && value.teacherType) {
        setValue('coach', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        setValue('coachAvailable', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        setValue('coachAll', emptySelectOption, { shouldDirty: true, shouldValidate: true });

        setSelectedTeacherType(value.teacherType);
        queryClient.invalidateQueries(getCoaches());
      }
    });
    return () => sub.unsubscribe();
  }, [setValue, watch, form, queryClient]);

  const formDateFields = (
    <>
      <div className="col-span-2">
        <FormRadioGroup label="Update effective date" form={form} name="updateType" options={allUpdateTypes} />
      </div>
      {updateType === ScheduleFromStudentGroupBulkActions.WithRange && (
        <>
          <FormInputDate name="startPlainDate" label="Start date" form={form} />
          <FormInputDate name="endPlainDate" label="End date" form={form} />
        </>
      )}
    </>
  );

  const groupClassTimes = studentGroups
    .flatMap((group) => group.classTime)
    .filter(distinct((classTime) => classTime.id));
  const hasValidAvailabilityDateRange = Temporal.PlainDate.compare(overrideStartPlainDate, overrideEndPlainDate) <= 0;
  const classTimeIdsForAvailability = useMemo(() => {
    if (bulkType === EditStudentGroupBulkActions.RecreateSessions) {
      return selectedClassTimeId ? [selectedClassTimeId] : [];
    }
    return groupClassTimes.map((classTime) => classTime.id);
  }, [bulkType, groupClassTimes, selectedClassTimeId]);
  const orgId = studentGroups[0]?.classTime?.schedule?.school.org.id || '';

  const coachesSearchOptions: GetCoachesModifier = {
    pageIndex: 0,
    filters: {
      availabilities: {
        classTimes: classTimeIdsForAvailability,
        orgId,
        ...(hasValidAvailabilityDateRange ? { overrideStartPlainDate, overrideEndPlainDate } : {}),
      },
    },
    ...(teacherAvailabilityV2Enabled ? { isNewLogic: true } : {}),
  };

  const allCoachesSearchOptions: CoachesTypeaheadSearchOptions = {
    shouldDisplayOverallGrade: true,
    filters: {
      statuses: [CoachStatus.ready_to_teach],
    },
  };

  const { data, isLoading: isLoadingCoaches } = useAvailableCoachesSearch(coachesSearchOptions, {
    shouldDisplayOverallGrade: true,
  });

  // Add the original coaches to the list
  const coachIds = useMemo(() => {
    return studentGroups.flatMap((sg) => (sg.coach?.id ? [sg.coach.id] : []));
  }, [studentGroups]);

  const originalCoachesQueries = useQueries({
    queries: coachIds.map((id) => ({
      ...getCoach(id),
      enabled: !!id,
    })),
  });

  const originalCoaches = useMemo(() => {
    return originalCoachesQueries.map((query) => query.data).filter((coach) => coach !== undefined);
  }, [originalCoachesQueries]);

  const availableCoaches = useMemo(() => {
    const baseCoaches = data || [];
    if (bulkType === EditStudentGroupBulkActions.RecreateSessions) {
      // In Recreate Sessions, "Available teachers only" must list only currently available teachers
      // for the selected schedule/class time.
      return baseCoaches;
    }

    const coachesToAdd: MultiSelectOption[] = [];

    for (const originalCoach of originalCoaches) {
      if (!baseCoaches.some((coach) => coach.value === originalCoach.id)) {
        coachesToAdd.push(buildCoachSelectOptions(originalCoach));
      }
    }

    return [...baseCoaches, ...coachesToAdd];
  }, [bulkType, data, originalCoaches]);

  const teacherSelect =
    selectedTeacherType === AllUpdateTeacherTypeValues.Available ? (
      <FormComboBox
        form={form}
        label={'Available teachers'}
        name={'coachAvailable'}
        placeholder="Select teacher"
        {...comboBoxOptionsOf(availableCoaches)}
        shouldHideCheck={true}
        renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
        emptySearchOptionMessage="No available teachers"
        isLoadingKnownOptions={isLoadingCoaches}
      />
    ) : (
      <FormComboBox
        form={form}
        label={'All teachers'}
        name={'coachAll'}
        placeholder="Select teacher"
        {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient, allCoachesSearchOptions))}
        shouldHideCheck={true}
        renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
      />
    );

  const getContent = () => {
    switch (bulkType) {
      case EditStudentGroupBulkActions.ExtendLastSession:
        return <div>This feature allows you to extend the last session of multiple classes at once.</div>;
      case EditStudentGroupBulkActions.UpdateTeacher:
        return (
          <>
            <div>This feature allows you to change the teacher of multiple classes at once.</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <FormRadioGroup label="New Teacher" form={form} name="teacherType" options={AllSelectTeacherTypes} />
              </div>
              <div className="col-span-2">{teacherSelect}</div>
              {formDateFields}
              {studentGroups[0]?.coach && (
                <>
                  <div className="col-span-2">Other</div>
                  <FormCheckbox
                    horizontal
                    name="reassigned"
                    label={`Count as a reassigned student group from ${studentGroups.length === 1 ? studentGroups[0]?.coach?.fullNameOrEmail : 'the original teacher'}`}
                    form={form}
                  />
                </>
              )}
            </div>
          </>
        );
      case EditStudentGroupBulkActions.UpdateLesson: {
        const selectedComboLearningPathId = watch('learningPath')?.value;
        const selectedLearningPath = availableLearningPaths.find((lp) => lp.id === selectedComboLearningPathId);
        const selectableLessons: MultiSelectOption[] = (selectedLearningPath?.learningPathLessons || []).map(
          ({ lesson }) => buildNameLikeSelectOptions(lesson),
        );

        if (selectedLearningPathId !== selectedComboLearningPathId) {
          setValue('startingLesson', { value: '', label: '' }, { shouldDirty: true });
          setSelectedLearningPathId(selectedComboLearningPathId);
        }

        return (
          <>
            <div>This feature allows you to change the starting lesson of multiple classes at once.</div>
            <div className="grid grid-cols-2 gap-4">
              <FormComboBox
                form={form}
                label="Learning path"
                name="learningPath"
                placeholder="Select learning path"
                {...comboBoxOptionsOf(learningPathOptions)}
                triggerProps={{ disabled: isLoadingLearningPaths }}
              />
              <FormComboBox
                form={form}
                label="Starting lesson"
                name="startingLesson"
                placeholder="Select starting lesson"
                {...comboBoxOptionsOf(selectableLessons)}
                triggerProps={{
                  disabled: isLoadingLearningPaths,
                }}
              />
              {formDateFields}
            </div>
          </>
        );
      }
      case EditStudentGroupBulkActions.UpdateStudents:
        return (
          <>
            <div>This feature allows you to change the students of multiple classes at once.</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <FormMultiSelect
                  form={form}
                  label="Students"
                  name="students"
                  multipleSelectorProps={{
                    placeholder: 'Search students by name to add to the group',
                    onSearch: userTypeaheadSearch(queryClient, { schools: [schoolId].filter(isNotNullish) }),
                  }}
                />
              </div>
              {formDateFields}
            </div>
          </>
        );
      case EditStudentGroupBulkActions.RemoveFutureSessions:
        return (
          <>
            <div>This feature allows you to remove the future sessions of multiple classes at once.</div>
            <div className="grid grid-cols-2 gap-4">{formDateFields}</div>
          </>
        );
      case EditStudentGroupBulkActions.RecreateSessions: {
        const selectedComboLearningPathId = watch('learningPath')?.value;
        const selectedLearningPath = availableLearningPaths.find((lp) => lp.id === selectedComboLearningPathId);
        const selectableLessons: MultiSelectOption[] = (selectedLearningPath?.learningPathLessons || []).map(
          ({ lesson }) => buildNameLikeSelectOptions(lesson),
        );

        if (selectedLearningPathId !== selectedComboLearningPathId) {
          // Only clear starting lesson when the user changed the learning path (not on initial sync from pre-filled)
          if (selectedLearningPathId !== undefined) {
            setValue('startingLesson', { value: '', label: '' }, { shouldDirty: true });
          }
          setSelectedLearningPathId(selectedComboLearningPathId);
        }

        return (
          <>
            <div>This feature allows you to recreate future sessions for a student group.</div>
            <div className="grid grid-cols-2 gap-1 mt-2">
              <FormComboBox
                form={form}
                label="Schedule"
                name="schedule"
                placeholder="Select schedule"
                {...comboBoxOptionsOf(scheduleOptions)}
                triggerProps={{
                  disabled: isLoadingSchedules,
                }}
                formDescription={
                  selectedSchedule ? (
                    <div>
                      Dates: {formatPlainDate(selectedSchedule.startPlainDate)} -{' '}
                      {formatPlainDate(selectedSchedule.endPlainDate)}
                    </div>
                  ) : null
                }
              />
              <FormComboBox
                form={form}
                label="Class time (CT)"
                name="classTime"
                placeholder="Select class times"
                {...comboBoxOptionsOf(filteredClassTimeOptions)}
                triggerProps={{
                  disabled: isLoadingSchedules,
                }}
              />
              <div className="col-span-2">
                <FormRadioGroup label="Teacher" form={form} name="teacherType" options={AllSelectTeacherTypes} />
              </div>
              <div className="col-span-2">{teacherSelect}</div>
              <div className="col-span-2">
                <FormMultiSelect
                  form={form}
                  label="Students"
                  name="students"
                  multipleSelectorProps={{
                    placeholder: 'Search students by name to add to the group',
                    onSearch: userTypeaheadSearch(queryClient, { schools: [schoolId].filter(isNotNullish) }),
                  }}
                />
              </div>
              <FormComboBox
                form={form}
                label="Learning path"
                name="learningPath"
                placeholder="Select learning path"
                {...comboBoxOptionsOf(learningPathOptions)}
                triggerProps={{ disabled: isLoadingLearningPaths }}
              />
              <FormComboBox
                form={form}
                label="Starting lesson"
                name="startingLesson"
                placeholder="Select starting lesson"
                {...comboBoxOptionsOf(selectableLessons)}
                triggerProps={{
                  disabled: isLoadingLearningPaths,
                }}
              />
              {formDateFields}
            </div>
          </>
        );
      }
      case EditStudentGroupBulkActions.ArchiveSessions: {
        const count = studentGroups.length;
        const classLabel = isOneTimeClass ? 'one-time classes' : 'classes';
        return (
          <div>
            {count} {classLabel} will be archived
          </div>
        );
      }
      case EditStudentGroupBulkActions.MergeClasses: {
        const selectedComboLearningPathId = watch('learningPath')?.value;
        const selectedLearningPath = availableLearningPaths.find((lp) => lp.id === selectedComboLearningPathId);
        const selectableLessons: MultiSelectOption[] = (selectedLearningPath?.learningPathLessons || []).map(
          ({ lesson }) => buildNameLikeSelectOptions(lesson),
        );

        if (selectedLearningPathId !== selectedComboLearningPathId) {
          setValue('startingLesson', { value: '', label: '' }, { shouldDirty: true });
          setSelectedLearningPathId(selectedComboLearningPathId);
        }

        return (
          <>
            <div>This feature allows you to merge multiple classes into one.</div>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <FormComboBox
                form={form}
                label="Teacher"
                name="coach"
                placeholder="Select teacher"
                {...comboBoxOptionsOf(
                  selectableCoaches?.length
                    ? selectableCoaches
                    : coachesTypeaheadSearch(queryClient, coachesSearchOptions),
                )}
                shouldHideCheck={true}
                renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
              />
              <FormMultiSelect
                form={form}
                label="Students"
                name="students"
                multipleSelectorProps={{
                  placeholder: 'Search students by name to add to the group',
                  onSearch: userTypeaheadSearch(queryClient, { schools: [schoolId].filter(isNotNullish) }),
                }}
              />
              <FormComboBox
                form={form}
                label="Learning path"
                name="learningPath"
                placeholder="Select learning path"
                {...comboBoxOptionsOf(learningPathOptions)}
                triggerProps={{ disabled: isLoadingLearningPaths }}
              />
              <FormComboBox
                form={form}
                label="Starting lesson"
                name="startingLesson"
                placeholder="Select starting lesson"
                {...comboBoxOptionsOf(selectableLessons)}
                triggerProps={{
                  disabled: isLoadingLearningPaths,
                }}
              />
            </div>
          </>
        );
      }
    }
  };

  return <div className="my-4 flex flex-col gap-4">{getContent()}</div>;
};
